/*
 * Copyright 2025 coze-dev Authors
 * Modifications Copyright 2026 Bytefinger Technology (HK) Co., Limited (https://bytefinger.ai)
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { type RefObject, useRef, useState } from 'react';

import classNames from 'classnames';
import { exhaustiveCheckSimple } from '@adp-common/chat-area-utils';
import { I18n } from '@adp-arch/i18n';
import { Tag, Typography } from '@coze-arch/coze-design';
import { MockHitStatus } from '@adp-arch/bot-api/debugger_api';

import { safeJSONParse } from '../../../utils/safe-json-parse';
import {
  type MockHitInfo,
  type FunctionCallMessageUnit,
} from '../../../utils/fucntion-call/types';
import {
  type FunctionCallMessageUnitGroup,
  getFunctionCallMessageUnitGroups,
  formatMessageTimeCost,
} from '../../../utils/fucntion-call/function-message-unit';
import { useShowBackGround } from '../../../hooks/public/use-show-bgackground';
import { useChatAreaContext } from '../../../hooks/context/use-chat-area-context';
import { type PreferenceContextInterface } from '../../../context/preference/types';
import { usePreference } from '../../../context/preference';
import { isToolResponseFailed } from './tool-status';
import { HarnessPlan } from './harness-plan';
import {
  HarnessExecution,
  delegationHasIncompleteResult,
} from './harness-execution';
import {
  CollapsePanelWithHeader,
  type CollapsePanelWithHeaderRef,
} from './collapse-panel-with-header';

import s from './index.module.less';

export interface FunctionCallMessagesCollapseProps {
  messageUnits: FunctionCallMessageUnit[];
  runTimeCost?: string;
  isRelatedChatComplete: boolean;
  isMessageFromOngoingChat: boolean;
  isFakeInterruptAnswer: boolean;
}

const HarnessToolSummaries = ({
  messageUnits,
  isMessageFromOngoingChat,
  isRelatedChatComplete,
  isFakeInterruptAnswer,
}: FunctionCallMessagesCollapseProps) => (
  <>
    <HarnessPlan units={messageUnits} />
    <HarnessExecution
      units={messageUnits}
      terminated={
        !isMessageFromOngoingChat &&
        !isRelatedChatComplete &&
        !isFakeInterruptAnswer
      }
    />
  </>
);

const getBackgroundColorByTheme = (
  theme: PreferenceContextInterface['theme'],
) => {
  if (theme === 'home') {
    return 'bg-[var(--coz-mg-card)]';
  }
  if (theme === 'debug' || theme === 'store') {
    return 'bg-[var(--coz-mg-primary)]';
  }
  exhaustiveCheckSimple(theme);
  return 'bg-[var(--coz-mg-primary)]';
};

const latestFunctionCall = (units: FunctionCallMessageUnit[]) => {
  const latest = units.at(-1);
  if (!latest) {
    throw new Error('empty FunctionCall messageList');
  }
  return latest;
};

export const FunctionCallMessagesCollapse: React.FC<
  FunctionCallMessagesCollapseProps
> = props => {
  const {
    messageUnits,
    runTimeCost,
    isMessageFromOngoingChat,
    isRelatedChatComplete,
    isFakeInterruptAnswer,
  } = props;
  const [isTopLevelOpen, setOpen] = useState(false);
  const messageUnitGroups = getFunctionCallMessageUnitGroups(messageUnits);
  const latestUnit = latestFunctionCall(messageUnits);
  const controlledItemRef = useRef<CollapsePanelWithHeaderRef>(null);
  const { configs } = useChatAreaContext();
  const { showFunctionCallDetail = true } = configs ?? {};
  const { theme } = usePreference();
  const onOpenChange = (v: boolean) => {
    if (v) {
      controlledItemRef.current?.open();
    }
    setOpen(v);
  };

  const isAllFunctionCallSuccess = !messageUnits.find(
    unit =>
      isToolResponseFailed(unit.apiResponse?.extra_info) ||
      delegationHasIncompleteResult(unit),
  );

  const hitMockSet = messageUnits.some(unit => {
    const mockHitInfo = safeJSONParse(
      unit.apiResponse?.extra_info.mock_hit_info ?? '{}',
    );
    return typeof mockHitInfo === 'object'
      ? (mockHitInfo as MockHitInfo).hitStatus === MockHitStatus.Success
      : false;
  });

  const showBackground = useShowBackGround();

  return (
    <>
      <HarnessToolSummaries {...props} />
      <CollapsePanelWithHeader
        messageUnit={latestUnit}
        isTopLevelOfTheNestedPanel
        isPanelOpen={isTopLevelOpen}
        isLatestFunctionCallOfRelatedChat={false}
        isMessageFromOngoingChat={isMessageFromOngoingChat}
        isRelatedChatComplete={isRelatedChatComplete}
        isRelatedChatAllFunctionCallSuccess={isAllFunctionCallSuccess}
        onOpenChange={onOpenChange}
        isFakeInterruptAnswer={isFakeInterruptAnswer}
        className={classNames(
          s['main-collapse'],
          [getBackgroundColorByTheme(theme), 'rounded-normal'],
          !isTopLevelOpen && 'h-48px',
          showBackground && '!coz-bg-image-bots',
        )}
        expandable={showFunctionCallDetail}
        hitMockSet={hitMockSet}
      >
        {(() => {
          let cursor = 0;
          const totalUnits = messageUnits.length;
          return messageUnitGroups.map(group => {
            if (group.kind === 'single_tool') {
              const unit = group.units[0];
              if (!unit) {
                return null;
              }
              const unitIndex = cursor;
              cursor += 1;
              const isLast = unitIndex === totalUnits - 1;
              const targetRef =
                !isRelatedChatComplete && isLast
                  ? controlledItemRef
                  : undefined;
              return (
                <CollapsePanelWithHeader
                  isTopLevelOfTheNestedPanel={false}
                  isRelatedChatAllFunctionCallSuccess={isAllFunctionCallSuccess}
                  key={group.id}
                  ref={targetRef}
                  isPanelOpen={isTopLevelOpen ? void 0 : false}
                  messageUnit={unit}
                  isLatestFunctionCallOfRelatedChat={isLast}
                  isRelatedChatComplete={isRelatedChatComplete}
                  isFakeInterruptAnswer={isFakeInterruptAnswer}
                  isMessageFromOngoingChat={isMessageFromOngoingChat}
                  expandable={showFunctionCallDetail}
                />
              );
            }
            // parallel_tools group
            const groupStart = cursor;
            cursor += group.units.length;
            return (
              <ParallelToolGroup
                key={group.id}
                group={group}
                groupStart={groupStart}
                totalUnits={totalUnits}
                isTopLevelOpen={isTopLevelOpen}
                isRelatedChatComplete={isRelatedChatComplete}
                isMessageFromOngoingChat={isMessageFromOngoingChat}
                isFakeInterruptAnswer={isFakeInterruptAnswer}
                isAllFunctionCallSuccess={isAllFunctionCallSuccess}
                showFunctionCallDetail={showFunctionCallDetail}
                controlledItemRef={controlledItemRef}
              />
            );
          });
        })()}
        {isRelatedChatComplete ? (
          <Tag
            color="green"
            className="my-4px mx-12px"
            data-testid="chat-area.fncall.bot_preview_run_completed"
          >
            <Typography.Text
              className="text-[12px] leading-[16px] font-normal coz-fg-hglt-green"
              ellipsis={{
                showTooltip: {
                  opts: {
                    content: formatMessageTimeCost(runTimeCost),
                    style: { wordBreak: 'break-word' },
                    position: 'topRight',
                    arrowPointAtCenter: false,
                  },
                },
              }}
            >
              {I18n.t('bot_preview_run_completed')}{' '}
              {formatMessageTimeCost(runTimeCost)}
            </Typography.Text>
          </Tag>
        ) : null}
        {!isMessageFromOngoingChat && !isRelatedChatComplete && (
          <Tag
            color="primary"
            data-testid="chat-area.fncall.bot_preview_run_terminated"
            className="my-4px mx-12px"
          >
            {I18n.t('bot_preview_run_terminated')}
          </Tag>
        )}
      </CollapsePanelWithHeader>
    </>
  );
};

interface ParallelToolGroupProps {
  group: FunctionCallMessageUnitGroup;
  groupStart: number;
  totalUnits: number;
  isTopLevelOpen: boolean;
  isRelatedChatComplete: boolean;
  isMessageFromOngoingChat: boolean;
  isFakeInterruptAnswer: boolean;
  isAllFunctionCallSuccess: boolean;
  showFunctionCallDetail: boolean;
  controlledItemRef: RefObject<CollapsePanelWithHeaderRef>;
}

const ParallelToolGroup: React.FC<ParallelToolGroupProps> = ({
  group,
  groupStart,
  totalUnits,
  isTopLevelOpen,
  isRelatedChatComplete,
  isMessageFromOngoingChat,
  isFakeInterruptAnswer,
  isAllFunctionCallSuccess,
  showFunctionCallDetail,
  controlledItemRef,
}) => {
  const llmText = formatMessageTimeCost(group.llmTime);
  const toolText =
    group.toolTime !== undefined
      ? formatMessageTimeCost(group.toolTime)
      : undefined;
  const totalText = formatMessageTimeCost(group.totalTime);
  const timingParts: string[] = [];
  if (llmText) {
    timingParts.push(`${I18n.t('debug_area_time_label_llm')} ${llmText}`);
  }
  if (toolText) {
    timingParts.push(`${I18n.t('debug_area_time_label_plugin')} ${toolText}`);
  }

  return (
    <div
      className={s['parallel-group']}
      data-testid="chat-area.fncall.parallel-group"
    >
      <div className={s['parallel-group-summary']}>
        <span className={s['parallel-group-title']}>
          {I18n.t('debug_area_time_label_plugin')}
          {group.toolName ? ` · ${group.toolName}` : ''}
        </span>
        <span className={s['parallel-group-badge']}>×{group.callCount}</span>
        {totalText ? (
          <span className={s['parallel-group-total']}>{totalText}</span>
        ) : null}
        {timingParts.length ? (
          <span className={s['parallel-group-timing']}>
            ({timingParts.join(' ｜ ')})
          </span>
        ) : null}
      </div>
      <div className={s['parallel-group-children']}>
        {group.units.map((unit, idx) => {
          const overallIndex = groupStart + idx;
          const isLast = overallIndex === totalUnits - 1;
          const targetRef =
            !isRelatedChatComplete && isLast ? controlledItemRef : undefined;
          const unitKey =
            unit.callId ??
            unit.llmOutput.message_id ??
            `${group.id}-${overallIndex}`;
          return (
            <CollapsePanelWithHeader
              isTopLevelOfTheNestedPanel={false}
              isRelatedChatAllFunctionCallSuccess={isAllFunctionCallSuccess}
              key={unitKey}
              ref={targetRef}
              isPanelOpen={isTopLevelOpen ? void 0 : false}
              messageUnit={unit}
              isLatestFunctionCallOfRelatedChat={isLast}
              isRelatedChatComplete={isRelatedChatComplete}
              isFakeInterruptAnswer={isFakeInterruptAnswer}
              isMessageFromOngoingChat={isMessageFromOngoingChat}
              expandable={showFunctionCallDetail}
            />
          );
        })}
      </div>
    </div>
  );
};
